import React, { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { X as ICON_CLOSE } from "react-feather"
import { SortableContainer, SortableElement, SortableHandle, SortEndHandler } from "react-sortable-hoc"
// REDUX
import { connect } from "react-redux"
import { mapDispatchProps, mapStateToProps } from "../../../store/store"
import Button from "../../../components/button"
// CORE
import API from "../../../api"
import { ROUTES } from "../../../routes"
// COMPONENTS
import TextInputField from "../../../components/textInput"
import ImageCropper from "../cropper"
// STYLE
import "./styles.css"

import { useLanguage } from "@languages/index"

interface SortableItemProps {
  value: any
}

interface SortableListProps {
  items: any[]
  onSortEnd: SortEndHandler
}

const EDIT_MODE_DEFAULT = "EDIT_MODE_DEFAULT"
const EDIT_MODE_ORDER = "EDIT_MODE_ORDER"

const SNSPostingCreate = (props: any) => {
  const navigate = useNavigate()
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.sns }
  const params = useParams()

  const postingId = useRef(params.postingId)

  const [editMode, setEditMode] = useState(EDIT_MODE_DEFAULT)

  const toggleEditMode = (mode) => {
    if (editMode == mode) {
      setEditMode(EDIT_MODE_DEFAULT)
    } else {
      setEditMode(mode)
    }
  }

  const [posting, setPosting] = useState<any>({
    products: [{}],
    images: [],
  })
  const [isFetching, setIsFetching] = useState(false)

  const postingImageInput = React.useRef(null)
  const profileImageInput = React.useRef(null)
  const productImageInput = React.useRef([])

  const fetchPosting = (postingId: any) => {
    setIsFetching(true)
    API.Post.show({
      brandToken: props.auth.token,
      postingId: postingId,
    })
      .then((response: any) => {
        // Model 자체에서 products 순서를 뒤집어놨는데 이유를 알 수 없음.
        // 엮인 곳이 많다보니 사이드이펙트가 우려되서 클라이언트단에서 해결
        if (response?.data?.posting?.products != null) {
          response.data.posting.products = response.data.posting.products.reverse()
        }
        setPosting(response.data.posting)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const renderLoadingIndicator = () => {
    return isFetching ? (
      <div className="flex flex-row mx-10 my-10 justify-center items-center">
        <div className="flex flex-row justify-center text-center my-5">
          <span className="prose prose-xl text-slate-400">{languageTexts.loading_1}</span>
        </div>
      </div>
    ) : null
  }

  useEffect(() => {
    if (postingId.current) fetchPosting(postingId.current)
  }, [])

  useEffect(() => {
    if (props.brand?.hasExperiencedSnsInstall && !props.brand?.snsPassInstalledAt) {
      navigate(ROUTES.SNS_PAYMENT_COMPLETE.PATH, { replace: true })
    }
  }, [])

  //   useEffect(() => {
  //     if (posting) {
  //       const copy = posting.products.map((item, index) => {
  //         return { ...item, ...{ index: index } };
  //       });

  //       const _posting = { ...posting, ...{ products: copy } };
  //       if (JSON.stringify(_posting) != JSON.stringify(posting)) {
  //         setPosting(_posting);
  //         console.log({ _posting: _posting });
  //       }
  //     }
  //   }, [posting]);

  const handleProductChange = async (index, key, value) => {
    const updatedProducts = posting.products.map((product, productIndex) => {
      if (productIndex === index) {
        return { ...product, [key]: value }
      }
      return product
    })

    await setPosting((prevPosting) => ({
      ...prevPosting,
      products: updatedProducts,
    }))
  }

  const handleUserChange = async (key, value) => {
    const copy = { ...{ [key]: value } }

    await setPosting({
      ...posting,
      ...copy,
    })
  }

  const removePostingImage = (image) => {
    if (image.id) {
      const url = image.thumbSmUrl.replace("https://nmodelin20.s3.ap-northeast-2.amazonaws.com/posting/thumb-sm/", "")

      API.SnsPosting.deleteImage({
        brandToken: props.auth.token,
        url: url,
      })
        .then((response: any) => {
          setPosting({
            ...posting,
            ...{
              images: posting.images.filter((item) => item.thumbSmUrl != image.thumbSmUrl),
            },
          })

          setTimeout(() => {
            props.showModal({
              text: response.message,
            })
          }, 500)
        })
        .catch((error) => {
          setTimeout(() => {
            props.showModal({
              text: error.message,
            })
          }, 500)
        })
    } else {
      setPosting({
        ...posting,
        ...{
          images: posting.images.filter((item) => item.thumbSmUrl != image.thumbSmUrl),
        },
      })
    }
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setPosting((prevPosting) => {
      const newImages = [...prevPosting.images]
      const [movedItem] = newImages.splice(oldIndex, 1)
      newImages.splice(newIndex, 0, movedItem)
      return { ...prevPosting, images: newImages }
    })
  }

  const DragHandle = SortableHandle(() => <span></span>)

  const renderImage = (image, index) => {
    return (
      <div key={index} className={`image-item inline relative`}>
        <img
          className="object-cover w-[124px] aspect-[0.7825]"
          onClick={() => {
            showCropModal(posting, index)
          }}
          key={`postingCardImage${image.id}`}
          src={image.thumbSmUrl}
          alt={`Image ${index + 1}`}
        />
        <div
          className="absolute top-0 right-0 bg-black"
          onClick={() => {
            props.showModal({
              title: languageTexts.create_text_1,
              text: languageTexts.create_text_2,
              cancel: () => {},
              confirm: () => {
                removePostingImage(image)
              },
            })
          }}
        >
          <div className="text-white">
            <ICON_CLOSE />
          </div>
        </div>
        <DragHandle />
      </div>
    )
  }

  const SortableItem = SortableElement<SortableItemProps>(({ index, value }) => {
    if (!value) return null

    return renderImage(value, index)
  })

  const SortableList = SortableContainer<SortableListProps>(({ items }) => (
    <div className="grid grid-cols-3 grid-flow-row gap-[14px] w-full">
      {items.map((value, index) => (
        <SortableItem key={`item-${index}`} index={index} value={value} />
      ))}
      {renderAddImageButton()}
    </div>
  ))

  const renderImages = () => {
    return (
      <div className="image-container ">
        {editMode == EDIT_MODE_ORDER ? (
          <SortableList items={posting?.images} onSortEnd={onSortEnd} axis="xy" />
        ) : (
          <div className="grid grid-cols-3 grid-flow-row gap-[14px] w-full">
            {posting?.images.map((value, index) => renderImage(value, index))}
            {renderAddImageButton()}
          </div>
        )}
      </div>
    )
  }

  const validate = (posting: any) => {
    const check = (posting) => {
      if (posting?.images == null || posting.images.length === 0) {
        return languageTexts.create_text_18
      }
      // MODEL DATA
      const input_validation_infos = [
        { key: "username", error_message: languageTexts.create_text_19 },
        { key: "age", error_message: languageTexts.create_text_20 },
        { key: "height", error_message: languageTexts.create_text_21 },
        { key: "topSize", error_message: languageTexts.create_text_22 },
        {
          key: "bottomSize",
          error_message: languageTexts.create_text_23,
        },
      ]
      for (let input_validation_info of input_validation_infos) {
        const value = posting[input_validation_info["key"]]
        if (value == null || value.trim().length === 0) return input_validation_info["error_message"]
      }
      // PRODUCTS DATA

      if (posting?.products == null || posting.products.length === 0) return languageTexts.create_text_24
      const product_validation_infos = [
        {
          key: "imageUrl",
          error_message: languageTexts.create_text_25,
        },
        { key: "name", error_message: languageTexts.create_text_26 },
        { key: "url", error_message: languageTexts.create_text_27 },
        {
          key: "colorName",

          error_message: languageTexts.create_text_28,
        },
        {
          key: "sizeName",
          error_message: languageTexts.create_text_29,
        },
      ]
      for (let product_data of posting.products) {
        for (let product_validation_info of product_validation_infos) {
          const value = product_data[product_validation_info["key"]]
          if (value == null || value.trim().length === 0) return product_validation_info["error_message"]
        }
      }
      return null
    }

    const errorMessage = check(posting)
    if (errorMessage != null && errorMessage.length > 0) {
      props.showModal({
        hideCloseButton: true,
        isCompact: true,
        text: errorMessage,
      })
      return false
    }
    return true
  }

  const storePosting = () => {
    const isValid = validate(posting)
    if (!isValid) return

    API.SnsPosting.store({
      brandToken: props.auth.token,
      posting: posting,
    })
      .then((response) => {
        props.showModal({ text: response.message })
        navigate(ROUTES.SNS_SHOW.SHOW(`${response.data.posting.id}`), {
          replace: true,
        })
      })
      .catch((error) => {
        props.showModal({ text: error.message })
      })
  }

  const updatePosting = () => {
    const isValid = validate(posting)
    if (!isValid) return

    API.SnsPosting.update({
      brandToken: props.auth.token,
      id: posting.id,
      posting: posting,
    })
      .then((response) => {
        props.showModal({ text: response.message })
        setPosting(response.data.posting)
        navigate(ROUTES.SNS_SHOW.SHOW(response.data.posting.id), {
          replace: true,
        })
      })
      .catch((error) => {
        props.showModal({ text: error.message })
      })
  }

  const handleImageChange = (target, e, productIndex?) => {
    const image = e.target.files[0]
    e.target.value = ""

    switch (target) {
      case "POSTING":
        API.Post.uploadImage({
          brandToken: props.auth.token,
          image: image,
        })
          .then(async (response) => {
            const copy = [
              ...posting.images,
              {
                thumbSmUrl: `https://nmodelin20.s3.ap-northeast-2.amazonaws.com/posting/thumb-sm/${response.data.url}`,
              },
            ]
            const _posting = { ...posting, ...{ images: copy } }
            await setPosting(_posting)
            showCropModal(_posting, _posting.images.length - 1)
          })
          .catch((error) => {
            props.showModal({ text: error.message })
          })
          .finally(() => {})
        break

      case "PROFILE":
        API.User.uploadImage({
          brandToken: props.auth.token,
          image: image,
        })
          .then((response) => {
            const copy = {
              ...posting,
              ...{
                profileImage: `${response.data.url}`,
                profileThumbUrl: `https://nmodelin20.s3.ap-northeast-2.amazonaws.com/profile/thumb-sm/${response.data.url}`,
                profileThumbSmUrl: `https://nmodelin20.s3.ap-northeast-2.amazonaws.com/profile/thumb-sm/${response.data.url}`,
              },
            }
            setPosting(copy)
          })
          .catch((error) => {
            props.showModal({ text: error.message })
          })
        break

      case "PRODUCT":
        API.Product.uploadImage({
          brandToken: props.auth.token,
          image: image,
        })
          .then(async (response) => {
            const productCopy = {
              ...posting.products[productIndex],
              imageUrl: `${response.data.url}`,
              thumbSmUrl: `https://nmodelin20.s3.ap-northeast-2.amazonaws.com/product/thumb-sm/${response.data.url}`,
            }

            const copy = [
              ...posting.products.map((item, itemIndex) => (itemIndex == productIndex ? productCopy : item)),
            ]

            await setPosting({ ...posting, ...{ products: copy } })
          })
          .catch((error) => {
            props.showModal({ text: error.message })
          })
          .finally(() => {})
        break
    }
  }

  const renderAddImageButton = () => {
    return (
      <div
        className="w-[124px] aspect-[0.7825] flex flex-col items-center justify-center text-center border border-[#E8E8E8] cursor-pointer"
        onClick={(event) => handleImageClick("POSTING", event)}
      >
        <span className="text-center">+</span>
        <input
          type="file"
          ref={postingImageInput}
          onChange={(event) => handleImageChange("POSTING", event)}
          style={{ display: "none" }}
        />
      </div>
    )
  }

  const removeProfileImage = () => {
    if (posting.profileImage) {
      API.SnsUser.deleteImage({
        brandToken: props.auth.token,
        userId: posting.userId,
        postingId: posting.id,
      })
        .then((response: any) => {
          setPosting({
            ...posting,
            ...{
              profileImage: null,
              profileThumbUrl: null,
              profileThumbSmUrl: null,
            },
          })
          setTimeout(() => {
            props.showModal({
              text: response.message,
            })
          }, 500)
        })
        .catch((error) => {
          setTimeout(() => {
            props.showModal({
              text: error.message,
            })
          }, 500)
        })
    } else {
      const userCopy = {
        ...posting.profileUser,
        ...{ profileImage: "" },
      }

      setPosting({
        ...posting,
        ...{ snsUser: userCopy },
      })
    }
  }
  const renderProfileImage = () => {
    if (!posting) {
      return null
    }

    if (!posting.products) {
      return null
    }

    return (
      <div className="flex flex-col justify-center items-center">
        <div className="flex flex-col gap-[0.75rem]">
          {posting.profileImage ? (
            <div
              className="relative cursor-pointer"
              onClick={(event) => (posting.profileImage ? null : handleImageClick("PROFILE", event))}
            >
              <img
                className="rounded-full overflow-hidden w-[7.75rem] h-[7.75rem] border border-[#E8E8E8]"
                src={posting.profileThumbUrl}
              />

              <div
                className="absolute top-0 right-0 bg-black"
                onClick={() => {
                  props.showModal({
                    title: languageTexts.create_text_3,
                    text: languageTexts.create_text_2,
                    cancel: () => {},
                    confirm: () => {
                      removeProfileImage()
                    },
                  })
                }}
              >
                <div className="text-white">
                  <ICON_CLOSE />
                </div>
              </div>
            </div>
          ) : (
            <div
              className="rounded-full overflow-hidden relative cursor-pointer"
              onClick={(event) => handleImageClick("PROFILE", event)}
            >
              <div className="rounded-full flex flex-col justify-center items-center border border-[#E8E8E8] w-[7.75rem] h-[7.75rem]">
                <span className="font-bold">+</span>
              </div>
            </div>
          )}

          <div className="flex flex-col justify-center items-center">
            <span>{languageTexts.create_text_4}</span>
          </div>
        </div>

        <input
          type="file"
          ref={profileImageInput}
          onChange={(event) => handleImageChange("PROFILE", event)}
          style={{ display: "none" }}
        />
      </div>
    )
  }

  const removeProductImage = (product) => {
    if (product.id) {
      API.SnsProduct.deleteImage({
        brandToken: props.auth.token,
        productId: product.id,
        postingId: product.postingId,
        url: product.thumbSmUrl.replace("https://nmodelin20.s3.ap-northeast-2.amazonaws.com/product/thumb-sm/", ""),
      })
        .then((response: any) => {
          setPosting({
            ...posting,
            ...{
              products: posting.products.map((item) =>
                item.thumbSmUrl == product.thumbSmUrl ? { ...item, thumbSmUrl: null, imageUrl: null } : item
              ),
            },
          })

          setTimeout(() => {
            props.showModal({
              text: response.message,
            })
          }, 500)
        })
        .catch((error) => {
          setTimeout(() => {
            props.showModal({
              text: error.message,
            })
          }, 500)
        })
    } else {
      setPosting({
        ...posting,
        ...{
          products: posting.products.map((item) =>
            item.thumbSmUrl == product.thumbSmUrl ? { ...item, thumbSmUrl: null, imageUrl: null } : item
          ),
        },
      })
    }
  }

  const renderProductImage = (product, index) => {
    if (!posting) {
      return null
    }

    if (!posting.products) {
      return null
    }

    if (!productImageInput.current) {
      return null
    }

    return (
      <div className="flex flex-col justify-center items-center">
        <div className="flex flex-col gap-[0.75rem]">
          <div className="overflow-hidden relative cursor-pointer w-[7.75rem] h-[7.75rem]">
            {product.imageUrl ? (
              <>
                <img className="w-full h-full border border-[#E8E8E8]" src={product.thumbSmUrl} />

                <div
                  className="absolute top-0 right-0 bg-black"
                  onClick={() => {
                    props.showModal({
                      title: languageTexts.create_text_5,
                      text: languageTexts.create_text_2,
                      cancel: () => {},
                      confirm: () => {
                        removeProductImage(product)
                      },
                    })
                  }}
                >
                  <div className="text-white">
                    <ICON_CLOSE />
                  </div>
                </div>
              </>
            ) : (
              <div
                className="w-full h-full flex flex-col justify-center items-center border border-[#E8E8E8]"
                onClick={(event: any) => handleImageClick("PRODUCT", null, productImageInput.current[index].click())}
              >
                <span className="font-bold">+</span>
              </div>
            )}
          </div>

          <div className="flex flex-col justify-center items-center">
            <span>{languageTexts.create_text_6}</span>
          </div>
        </div>

        <input
          type="file"
          ref={(el) => (productImageInput.current[index] = el)}
          onChange={(event) => handleImageChange("PRODUCT", event, index)}
          style={{ display: "none" }}
        />
      </div>
    )
  }

  const handleImageClick = (target, e, callback?) => {
    switch (target) {
      case "POSTING":
        postingImageInput.current.click()
        break

      case "PROFILE":
        profileImageInput.current.click()
        break

      case "PRODUCT":
        callback ? callback() : null
        break
    }
  }

  const renderProductsInput = (products: any) => {
    return products.map((product, index) => {
      return (
        <div className="flex flex-col gap-[0.625rem]" key={`products-${index}`}>
          {renderProductImage(product, index)}

          <TextInputField
            bordered
            borderColor="#E8E8E8"
            placeholder={languageTexts.product_name}
            value={product?.name ?? ""}
            onChangeText={(value) => {
              handleProductChange(index, "name", value)
            }}
          />
          <TextInputField
            bordered
            borderColor="#E8E8E8"
            placeholder={"URL"}
            value={product?.url ?? ""}
            onChangeText={(value) => {
              handleProductChange(index, "url", value)
            }}
          />
          <TextInputField
            bordered
            borderColor="#E8E8E8"
            placeholder={languageTexts.color}
            value={product?.colorName ?? ""}
            onChangeText={(value) => {
              handleProductChange(index, "colorName", value)
            }}
          />
          <TextInputField
            bordered
            borderColor="#E8E8E8"
            placeholder={languageTexts.size}
            value={product?.sizeName ?? ""}
            onChangeText={(value) => {
              handleProductChange(index, "sizeName", value)
            }}
          />

          {index > 0 ? (
            <Button
              text={languageTexts.create_text_7}
              onClick={() => {
                removeProduct(product)
              }}
            />
          ) : null}
        </div>
      )
    })
  }

  const renderAddProductButton = () => {
    return (
      <div className="flex flex-col">
        <Button
          text={languageTexts.uploader_text_6}
          onClick={() => {
            addProduct()
          }}
        />
      </div>
    )
  }

  const renderSubmitButton = () => {
    return (
      <div className="flex flex-col py-[1.875rem]">
        <Button
          text={posting && posting.id ? languageTexts.create_text_8 : languageTexts.create_text_9}
          buttonColor={"BLACK"}
          textColor={"WHITE"}
          onClick={() => {
            posting && posting.id ? updatePosting() : storePosting()
          }}
        />
      </div>
    )
  }

  const addProduct = () => {
    const copy = [...posting.products, {}]
    setPosting({ ...posting, ...{ products: copy } })
  }

  const removeProduct = (product?: any) => {
    const productsCopy = [...posting.products].filter((item) => {
      return item.thumbSmUrl != product.thumbSmUrl
    })

    setPosting({ ...posting, ...{ products: productsCopy } })
  }

  const showCropModal = (posting, imageIndex) => {
    let imageUrl = posting.images[imageIndex].thumbSmUrl.replace("/thumb-sm/", "/thumb/")
    if (posting.images[imageIndex].imgUrlCropped) {
      imageUrl = imageUrl.replace("/thumb", "")
    }

    props.showModal({
      content: () => (
        <ImageCropper
          {...props}
          image={posting.images[imageIndex]}
          index={imageIndex}
          url={imageUrl}
          onUpdate={(img, updatedIndex, response) => {
            const updatedImages = [...posting.images]
            updatedImages[updatedIndex].thumbSmUrl = response.data.url

            const _posting = {
              ...posting,
              images: updatedImages,
            }

            setPosting(_posting)
          }}
        />
      ),
    })
  }

  const render = () => {
    if (!props.brand?.hasExperiencedSnsInstall) {
      return null
    }

    if (!props.brand?.snsPassInstalledAt) {
      return null
    }

    if (!posting) {
      return null
    }

    return (
      <div className="flex flex-col justify-center">
        <div className="flex flex-col w-full max-w-[1025px] mt-[4.25rem] mx-auto gap-[3.75rem]">
          <div
            className="flex flex-row gap-[0.625rem] cursor-pointer"
            onClick={() => {
              history.back()
            }}
          >
            <img src={"/assets/icons/ic-chevron-left-black.svg"} />
            <span className="font-bold text-[1.375rem]">{languageTexts.button_prev_2}</span>
          </div>
        </div>

        <div className="flex flex-col w-full max-w-[392px] mb-[4.25rem] mx-auto gap-[3.75rem]">
          <div className="flex flex-col gap-[3.125rem]">
            <div className="flex flex-row justify-center pt-[2.5rem]">
              <span className="font-bold text-[1.375rem]">
                {posting && posting.id ? languageTexts.create_text_8 : languageTexts.create_text_9}
              </span>
            </div>

            <div className="flex flex-col gap-[3.125rem]">
              <div className="flex flex-col gap-[15px]">
                <div className="flex flex-row justify-between">
                  <span className="font-bold text-base">{languageTexts.create_text_10}</span>
                  <span
                    className="font-bold text-base text-[#999999] hover:text-black"
                    onClick={() => {
                      toggleEditMode(EDIT_MODE_ORDER)
                    }}
                  >
                    {editMode == EDIT_MODE_ORDER ? languageTexts.done : languageTexts.create_text_11}
                  </span>
                </div>

                <div className="overflow-x-visible">{renderImages()}</div>
              </div>
              <div className="flex flex-col gap-[15px]">
                <div>
                  <span className="font-bold text-base">{languageTexts.create_text_12}</span>
                </div>

                <div className="flex flex-col gap-[0.625rem]">
                  {renderProfileImage()}

                  <TextInputField
                    bordered
                    borderColor="#E8E8E8"
                    onChangeText={(value) => {
                      handleUserChange("username", value)
                    }}
                    placeholder={languageTexts.name}
                    value={posting?.username ?? ""}
                  />
                  <TextInputField
                    bordered
                    onChangeText={(value) => {
                      handleUserChange("age", value)
                    }}
                    borderColor="#E8E8E8"
                    placeholder={languageTexts.age}
                    value={posting?.age ?? ""}
                  />
                  <TextInputField
                    bordered
                    borderColor="#E8E8E8"
                    onChangeText={(value) => {
                      handleUserChange("height", value)
                    }}
                    placeholder={languageTexts.height}
                    value={posting?.height ?? ""}
                  />
                  <TextInputField
                    bordered
                    borderColor="#E8E8E8"
                    onChangeText={(value) => {
                      handleUserChange("topSize", value)
                    }}
                    placeholder={languageTexts.create_text_13}
                    value={posting?.topSize ?? ""}
                  />
                  <TextInputField
                    bordered
                    borderColor="#E8E8E8"
                    placeholder={languageTexts.create_text_14}
                    onChangeText={(value) => {
                      handleUserChange("bottomSize", value)
                    }}
                    value={posting?.bottomSize ?? ""}
                  />
                  <TextInputField
                    bordered
                    borderColor="#E8E8E8"
                    onChangeText={(value) => {
                      handleUserChange("shoesSize", value)
                    }}
                    placeholder={languageTexts.create_text_15}
                    value={posting?.shoesSize ?? ""}
                  />
                  <TextInputField
                    bordered
                    borderColor="#E8E8E8"
                    onChangeText={(value) => {
                      handleUserChange("braSize", value)
                    }}
                    placeholder={languageTexts.create_text_16}
                    value={posting?.braSize ?? ""}
                  />
                  <TextInputField
                    bordered
                    borderColor="#E8E8E8"
                    placeholder={languageTexts.create_text_17}
                    onChangeText={(value) => {
                      handleUserChange("pantieSize", value)
                    }}
                    value={posting?.pantieSize ?? ""}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-[15px]">
                <div>
                  <span className="font-bold text-base">{languageTexts.celeb_detail_text_8}</span>
                </div>
                <div className="text-xs tracking-tighter text-gray-500">{languageTexts.create_text_30}</div>

                <div className="flex flex-col gap-[1.875rem]">
                  {renderProductsInput(posting.products ? posting.products : [{}])}
                </div>

                {renderAddProductButton()}
                {renderSubmitButton()}
              </div>
            </div>
          </div>

          {renderLoadingIndicator()}
        </div>
      </div>
    )
  }

  return <>{render()}</>
}

export default connect(mapStateToProps, mapDispatchProps)(SNSPostingCreate)
